import { Directive, inject, OnDestroy, OnInit } from '@angular/core';
import { MobileDialogBackButtonService } from '@shared/mobile-dialog-back-button.service';
import { NzDrawerComponent } from 'ng-zorro-antd/drawer';
import { ReplaySubject, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Directive({
  // eslint-disable-next-line @angular-eslint/directive-selector
  selector: 'nz-drawer',
  standalone: false,
})
export class NzDrawerMobileBackButtonDirective implements OnInit, OnDestroy {
  private readonly drawer = inject(NzDrawerComponent);
  private readonly mobileDialogBackButton = inject(MobileDialogBackButtonService);
  private readonly isPhone = this.mobileDialogBackButton.isPhone();
  private readonly destroy$: ReplaySubject<boolean> = new ReplaySubject(1);
  private closed$: Subject<void> | null = null;

  constructor() {
    // Must be set before the drawer creates its overlay, which happens before this directive's ngOnInit.
    if (this.isPhone) {
      this.drawer.nzCloseOnNavigation = false;
    }
  }

  public ngOnInit(): void {
    if (!this.isPhone) {
      return;
    }

    this.drawer.nzVisibleChange.pipe(takeUntil(this.destroy$)).subscribe(visible => {
      if (visible) {
        this.handleOpened();
        return;
      }

      this.handleClosed();
    });

    if (this.drawer.isOpen) {
      this.handleOpened();
    }
  }

  public ngOnDestroy(): void {
    this.handleClosed();
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  private handleOpened(): void {
    if (this.closed$) {
      return;
    }

    this.closed$ = new Subject<void>();
    this.mobileDialogBackButton.registerOpenOverlay({
      isOpen: () => this.drawer.isOpen,
      close: () => {
        // Same as a mask click, so the parent's [nzVisible] binding stays in sync.
        this.drawer.nzOnClose.emit();
        return true;
      },
      closed: this.closed$,
      navigationClose: () => this.drawer.close(),
    });
  }

  private handleClosed(): void {
    this.closed$?.next();
    this.closed$?.complete();
    this.closed$ = null;
  }
}
